import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getPublicApiBaseUrl } from "@/utils/apiBaseUrl";
import { bearerAuthHeaders } from "@/utils/bearerAuthHeaders";

interface LoginRequest {
  email: string;
  password: string;
  deviceToken?: string;
  deviceType?: string;
}

interface RegisterRequest {
  full_name: string;
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  zip_code: string;
  phone_number?: string;
  userType: string;
  image?: File;
  "location[type]": string;
  "location[coordinates][]": number[];
}

interface AuthResponse {
  data: {
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      fullName?: string;
      image?: string;
    };
    access_token: string;
  };
  success: boolean;
  message: string;
}

interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      role?: string;
    };
    token: string;
    nearbyGroupSuggestion?: any;
  };
}

/** Standard API envelope + Google auth URL payload */
export interface GoogleAuthUrlResponse {
  success: boolean;
  message: string;
  code: number;
  data?: {
    url: string;
  };
}

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${getPublicApiBaseUrl()}api/auth`,
  }),
  endpoints: (builder) => ({
    getGoogleAuthUrl: builder.query<GoogleAuthUrlResponse, void>({
      query: () => "/google",
    }),
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: "/login",
        method: "POST",
        body: credentials,
      }),
    }),
    registerUser: builder.mutation({
      query: (formData) => ({
        url: "/register",
        method: "POST",
        body: formData,
      }),
    }),
    VerifyUser: builder.mutation({
      query: ({ body, token }) => ({
        url: "/verify-otp",
        method: "POST",
        body: body,
        headers: bearerAuthHeaders(token),
      }),
    }),
    ResendOTP: builder.mutation({
      query: ({ body, token }) => ({
        url: "/resend-otp",
        method: "POST",
        body: body,
        headers: bearerAuthHeaders(token),
      }),
    }),
     Logout: builder.mutation({
      query: ({ body, token }) => ({
        url: "/logout",
        method: "POST",
        body: body,
        headers: bearerAuthHeaders(token),
      }),
    }),

    ForgotPassword: builder.mutation({
      query: (body) => ({
        url: "/forgot-password",
        method: "POST",
        body: body,
      }),
    }),

    ResetPassword: builder.mutation({
      query: ({ body, token }) => ({
        url: "/reset-password",
        method: "POST",
        body,
        headers: bearerAuthHeaders(token),
      }),
    }),

    CreateUserProfile: builder.mutation({
      query: ({ body, token }) => ({
        url: `/create-profile`,
        method: "POST",
        body,
        headers: bearerAuthHeaders(token),
      }),
    }),
    JoinNeighbourlyGroup: builder.mutation({
      query: ({ body, token, id }) => ({
        url: `/join-nearby-group`,
        method: "POST",
        body,
        headers: bearerAuthHeaders(token),
      }),
    }),
    NotificationToggle: builder.mutation({
      query: ({ token }) => ({
        url: `/notification-toggle`,
        method: "POST",
        headers: bearerAuthHeaders(token),
      }),
    }),
     UpdateProfile: builder.mutation({
      query: ({ body, token}) => ({
        url: `/edit-profile`,
        method: "PUT",
        body,
        headers: bearerAuthHeaders(token),
      }),
    }),
    ChangePassword: builder.mutation({
      query: ({ body, token}) => ({
        url: `/change-password`,
        method: "PUT",
        body,
        headers: bearerAuthHeaders(token),
      }),
    }),

      getUserbyId: builder.query({
      query: (options) => {
        return {
          url: `/profile/${options?.id}`,
          headers: bearerAuthHeaders(options?.token),
        };
      },
    }),
  }),
});

export const {
  useLazyGetGoogleAuthUrlQuery,
  useLoginMutation,
  useRegisterUserMutation,
  useVerifyUserMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useCreateUserProfileMutation,
  useJoinNeighbourlyGroupMutation,
  useNotificationToggleMutation,
  useResendOTPMutation,
  useUpdateProfileMutation,
  useChangePasswordMutation,
  useGetUserbyIdQuery,
  useLogoutMutation
  
} = authApi;
